import { Component, OnInit, HostListener } from '@angular/core';
import { Location } from '@angular/common';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Observable } from 'rxjs/Observable';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { Observer } from 'rxjs/Observer';
import * as _ from 'lodash';
import * as stringify from 'json-stable-stringify';
import { SharedService } from 'app/shared/services/shared.service';
import { HttpService } from 'app/shared/services/http.service';
import { DictionaryService } from 'app/shared/services/dictionary.service';
import { ModalsService } from 'app/shared/services/modals.service';
import { UserService } from 'app/shared/services/user.service';
import { GoogleAnalyticsService } from 'app/shared/services/google-analytics.service';
import { SupplyService } from 'app/shared/services/supply.service';
import { CallbacksService } from 'app/shared/services/callbacks.service';

const CALLBACKS_PER_TYPE = 4;

@AutoUnsubscribe()
@Component({
  selector: 'network-settings-component',
  templateUrl: './network-settings.component.html',
  styleUrls: ['./network-settings.component.scss'],
})
export class NetworkSettingsComponent implements OnInit {
  public allSections: Array<any>;
  public activeSection: number;
  public networkSettings: any;
  public originalData: any;
  public accessKey: any;
  public accessKeyGenerated: boolean;
  public encryptionKey: any;
  public encryptionKeyGenerated: boolean;
  public encryptionTestSource: number;
  public encryptionTestTarget: string;
  public postbidValues: Array<number>;
  public settingsLoaded: number;
  public settingsTotal: number;
  public approved: boolean;
  public eventsList: Array<any>;
  public errorInputs: Array<string>;
  public isLoading: boolean;
  public isLoadingCallbacks: boolean;
  public subscriptions: Array<any>;
  public shouldShowPostbid: boolean;
  public networkIsChanging: boolean;
  public isUserAdmin: boolean;

  constructor(
    public location: Location,
    public sharedService: SharedService,
    public httpService: HttpService,
    public dictionaryService: DictionaryService,
    public modalsService: ModalsService,
    public userService: UserService,
    public supplyService: SupplyService,
    public callbacksService: CallbacksService,
    public ga: GoogleAnalyticsService
  ) {
    this.isUserAdmin = this.userService.isAdmin();
    this.allSections = [
      {
        name: 'PREBID',
        value: 'prebidSection',
        toggleStatus: true,
        hideFromPublishersNetwork: true,
        featureFlag: 'pre_bid',
      },
      {
      //   name: 'POSTBID',
      //   value: 'postbidSection',
      //   toggleStatus: true,
      //   hideFromPublishersNetwork: true,
      //   featureFlag: 'post_bid',
      },
      { name: 'CALLBACKS', value: 'callbacksSection', toggleStatus: true },
      { name: 'PLATFORM_API_KEY', value: 'accessKeySection', toggleStatus: true },
      { name: 'ENCRYPTION_KEY', value: 'encryptionKeySection', toggleStatus: true },
    ];
    if (this.isUserAdmin) {
      this.allSections.push({
        name: 'PREBID_BUNDLE',
        value: 'prebidBundleSection',
        toggleStatus: true,
        featureFlag: 'network_prebid_json',
      });
    }
    this.activeSection = 0;
    this.networkSettings = {};
    this.originalData = _.cloneDeep(this.networkSettings);
    this.postbidValues = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    this.settingsLoaded = 0;
    this.settingsTotal = 0;
    this.approved = false;
    this.shouldShowPostbid = false;
    this.eventsList = [];
    this.supplyService.eventsList = [
      { name: this.dictionaryService.get('PLAYER_READY'), eventId: 1, typeId: 0 },
      { name: this.dictionaryService.get('AD_START'), eventId: 2, typeId: 0 },
      { name: this.dictionaryService.get('AD_COMPLETE'), eventId: 3, typeId: 0 },
      { name: this.dictionaryService.get('AD_CLICK'), eventId: 4, typeId: 0 },
      { name: this.dictionaryService.get('IVT_DETECTION'), eventId: 2, typeId: 1 },
    ];
    this.errorInputs = [];
    this.isLoading = true;
    this.isLoadingCallbacks = true;
    this.subscriptions = [];
    this.networkIsChanging = false;
  }

  ngOnDestroy(): void {}

  ngOnInit(): void {
    this.initialize();
    this.subscriptions.push(
      this.sharedService.getMessagesBus().subscribe((response: any) => {
        if (response.type === 'NETWORKS_WILL_BE_CHANGED') {
          this.networkIsChanging = true;
        } else if (response.type === 'NETWORK_CHANGE') {
          this.initialize();
        } else if (response.type === 'REGENERATE_KEY_CONFIRM') {
          if (response.data === 'access') {
            this.generateAccessKey();
          } else if (response.data === 'encryption') {
            this.generateEncryptionKey();
          }
        } else if (response.type === 'CONFIRM_OPT_OUT') {
          this.approved = true;
          this.networkSettings.postbid = false;
          this.savePostBid().subscribe();
        } else if (response.type === 'MODAL_HIDE') {
          if (response.data.component === 'postbid-opt-out') {
            if (this.approved !== true) {
              this.networkSettings.postbid = true;
            }
          }
        }
      })
    );
    this.sharedService.setPageTitle('NETWORK_SETTINGS');
  }

  buildNetworkSettings(): any {
    const settings: any = {
      prebid: {
        anonymous: false,
        fraud: false,
      },
      postbid: false,
      ivt: [
        {
          ivt_partner_id: 1,
          rate: 1,
        },
      ],
      callbacks: [],
      isEUExclude: false,
    };

    if (this.userService.getFeatureState('network_prebid_json') && this.isUserAdmin) {
      settings.prebidBundle = {
        url: '',
        json: '',
      };
    }

    this.networkSettings = settings;
    this.settingsTotal = Object.keys(settings).length;
  }

  initialize(): void {
    this.isLoading = true;
    this.settingsLoaded = 0;

    this.buildNetworkSettings();
    this.getNetworkPreBidSettings();
    this.getNetworkIvt();
    this.getNetworkCallbacks();
    this.getNetworkEUStatus();
    this.getApiKeys();
    this.getActiveKey();
    if (this.userService.getFeatureState('network_prebid_json') && this.isUserAdmin) {
      this.getNetworkPreBidBundle();
    }
    this.shouldShowPostbid = this.isShouldShowPostbid();
    if (this.userService.isPublisher() || this.userService.isPublisherNetwork()) {
      this.activeSection = 3;
    }
  }

  getActiveSectionDefaults(): number {
    let section = 0;
    if (!this.userService.getFeatureState('pre_bid')) {
      section = 1;
      if (!this.userService.getFeatureState('post_bid')) {
        section = 2;
      }
    }
    return section;
  }

  getNetworkPreBidSettings(): void {
    this.subscriptions.push(
      this.httpService.post('vdsp_ws/getNetworkPreBidSettings').subscribe(
        (response: any) => {
          this.networkSettings.prebid = _.cloneDeep(response);
          this.onSettingsLoaded();
        },
        () => {
          this.sharedService.showErrorFlashMessage();
          this.isLoading = false;
        }
      )
    );
  }

  getNetworkIvt(): void {
    this.subscriptions.push(
      this.httpService.post('vdsp_ws/getNetworkIvt').subscribe(
        (response: any) => {
          const ivt: Array<any> = _.cloneDeep(response);
          this.networkSettings.postbid = _.filter(ivt, (item: any) => +item.rate > 0).length > 0;
          ivt.forEach((item: any) => {
            const rate: number = +item.rate;
            item.rate =
              rate > 0 ? _.findIndex(this.postbidValues, (item: number) => +item.toString() === +rate.toString()) : 0;
          });
          this.networkSettings.ivt = ivt;
          this.onSettingsLoaded();
        },
        () => {
          this.sharedService.showErrorFlashMessage();
          this.isLoading = false;
        }
      )
    );
  }

  getNetworkPreBidBundle(): void {
    this.subscriptions.push(
      this.httpService.post('vdsp_ws/getNetworkPreBidBundle').subscribe(
        (response: any) => {
          const resp: any = _.cloneDeep(response.data);
          const bundle: any = this.sharedService.isJSON(resp) ? JSON.parse(resp) : null;
          if (bundle) {
            this.networkSettings.prebidBundle.url = decodeURI(bundle.src);
            this.networkSettings.prebidBundle.json = stringify(_.omit(bundle, 'src'), {
              space: this.sharedService.formatJSONstring,
            });
          }
          this.onSettingsLoaded();
        },
        () => {
          this.sharedService.showErrorFlashMessage();
          this.isLoading = false;
        }
      )
    );
  }

  getNetworkEUStatus(): void {
    this.subscriptions.push(
      this.httpService.post('networks_management/getNetworkTargeting').subscribe((response: any) => {
        this.networkSettings.isEUExclude =
          response && response.country_codes.includes('EU') && response.country_codes_oper == 2;
      })
    );
  }

  getNetworkCallbacks(): void {
    this.isLoadingCallbacks = true;
    this.subscriptions.push(
      this.httpService.post('networks_management/getCallbacks').subscribe(
        (response: any) => {
          const callbacksResponse: Array<any> = _.cloneDeep(response);
          const callbacks: Array<any> = [];
          let currEvent: any;
          callbacksResponse.forEach((callback: any) => {
            currEvent = _.find(this.supplyService.eventsList, (evt: any) => {
              return evt.eventId === +callback.eventId && evt.typeId === +callback.typeId;
            });
            if (currEvent) {
              callbacks.push({
                callbackId: +callback.callbackId,
                url: callback.url,
                event: currEvent,
                rate: +callback.rate,
              });
            }
          });
          this.networkSettings.callbacks = callbacks;
          this.isLoadingCallbacks = false;
          this.onSettingsLoaded();
        },
        () => {
          this.sharedService.showErrorFlashMessage();
        }
      )
    );
  }

  getApiKeys(): void {
    this.subscriptions.push(
      this.httpService.post('api_management/getApiKeys').subscribe(
        (response: any) => {
          this.accessKeyGenerated = !this.sharedService.isEmpty(response.data.id);
          this.accessKey = this.accessKeyGenerated
            ? response.data
            : {
                active: '-',
                created: '-',
                id: '-',
                secret: '-',
              };
          this.onSettingsLoaded();
        },
        () => {
          this.sharedService.showErrorFlashMessage();
          this.isLoading = false;
        }
      )
    );
  }

  getActiveKey(): void {
    this.subscriptions.push(
      this.httpService.post('networks_management/getActiveKey').subscribe(
        (response: any) => {
          this.encryptionKeyGenerated = !this.sharedService.isEmpty(response.data.generatedKey);
          this.encryptionKey = this.encryptionKeyGenerated
            ? response.data
            : {
                active: '-',
                created: '-',
                generatedKey: '-',
              };
          this.onSettingsLoaded();
        },
        () => {
          this.sharedService.showErrorFlashMessage();
          this.isLoading = false;
        }
      )
    );
  }

  onSettingsLoaded(): void {
    this.settingsLoaded++;
    if (this.settingsLoaded === this.settingsTotal) {
      this.originalData = _.cloneDeep(this.networkSettings);
      this.isLoading = false;
      setTimeout(() => {
        this.networkIsChanging = false;
        window.scrollTo(0, 0);
        this.activeSection = this.getActiveSectionDefaults();
      });
    }
  }

  toggleSection(id: number): void {
    this.allSections[id].toggleStatus = !this.allSections[id].toggleStatus;
  }

  goToSection(id: number): void {
    const section = document.getElementById(this.allSections[id].value);
    const yPosition = id > 0 ? section.offsetTop : 0;
    window.scroll({ top: yPosition, left: 0, behavior: 'smooth' });
    this.activeSection = id;
    if (!this.allSections[id].toggleStatus) {
      this.toggleSection(id);
    }
    setTimeout(() => {
      this.activeSection = id;
    }, 400);
  }

  getSectionsPosition(): number {
    return window.scrollY > 72 ? 0 : 72;
  }

  getSectionsArrowPosition(): any {
    const activeSection: HTMLElement = document.getElementById('sectionButton' + this.activeSection);
    return activeSection ? activeSection.offsetTop : 0;
  }

  isSectionActive(id: number): boolean {
    let answer = true;
    const featureFlag: string = this.allSections[id].featureFlag;
    if (this.userService.isPublisher() || this.userService.isPublisherNetwork()) {
      answer = this.allSections[id].hideFromPublishersNetwork !== true;
    }
    if (featureFlag) {
      answer = answer && this.userService.getFeatureState(featureFlag);
    }
    return answer;
  }

  getCallbackEvents(currItem: any): Array<any> {
    const availableCallbacks: Array<any> = [];
    let isAvailable: boolean;
    let callbacksOfCurrType: Array<any>;
    this.eventsList = [];
    for (let i = 0; i < this.supplyService.eventsList.length; i++) {
      isAvailable = false;
      if (this.supplyService.eventsList[i].featureFlag) {
        if (this.userService.getFeatureState(this.supplyService.eventsList[i].featureFlag)) {
          isAvailable = true;
        }
      } else {
        isAvailable = true;
      }
      if (isAvailable) {
        callbacksOfCurrType = _.filter(this.networkSettings.callbacks, { event: this.supplyService.eventsList[i] });
        if (callbacksOfCurrType.length < CALLBACKS_PER_TYPE || currItem.event === this.supplyService.eventsList[i]) {
          availableCallbacks.push(this.supplyService.eventsList[i]);
        }
      }
    }
    return availableCallbacks;
  }

  getAvailableCallbacks(): Array<number> {
    const currCallbacks: Array<number> = _.map(this.networkSettings.callbacks, 'event');
    const availableCallbacks: Array<number> = [];
    for (let i = 0; i < this.supplyService.eventsList.length; i++) {
      if (
        _.filter(currCallbacks, {
          eventId: this.supplyService.eventsList[i].eventId,
          typeId: this.supplyService.eventsList[i].typeId,
        }).length < CALLBACKS_PER_TYPE
      ) {
        availableCallbacks.push(this.supplyService.eventsList[i]);
      }
    }
    return availableCallbacks;
  }

  addCallback(): void {
    this.networkSettings.callbacks.push({
      url: '',
      event: this.getAvailableCallbacks()[0],
      rate: 100,
    });
  }

  removeCallback(callbackIndex: number): void {
    this.networkSettings.callbacks.splice(callbackIndex, 1);
  }

  viewAvailableMacros(): void {
    this.modalsService.showModal({
      component: 'available-macros',
      title: this.dictionaryService.get('AVAILABLE_MACROS'),
      data: { type: 'supply' },
    });
  }

  onGenerateAccessKeyClick(): void {
    if (this.accessKeyGenerated) {
      this.modalsService.showModal({
        component: 'regenerate-key-confirm',
        title: this.dictionaryService.get('WARNING'),
        data: { type: 'access' },
      });
    } else {
      this.accessKeyGenerated = true;
      this.generateAccessKey();
    }
  }

  generateAccessKey(): void {
    this.isLoading = true;
    this.subscriptions.push(
      this.httpService.post('api_management/generateApiKey').subscribe(
        (response: any) => {
          this.accessKey = response.data;
          this.isLoading = false;
        },
        () => {
          this.sharedService.showErrorFlashMessage();
          this.isLoading = false;
        }
      )
    );
  }

  onGenerateEncryptionKeyClick(): void {
    if (this.encryptionKeyGenerated) {
      this.modalsService.showModal({
        component: 'regenerate-key-confirm',
        title: this.dictionaryService.get('WARNING'),
        data: { type: 'encryption' },
      });
    } else {
      this.encryptionKeyGenerated = true;
      this.generateEncryptionKey();
    }
  }

  generateEncryptionKey(): void {
    this.isLoading = true;
    this.subscriptions.push(
      this.httpService.post('networks_management/generateKey').subscribe(
        (response: any) => {
          this.encryptionKey = response.data;
          this.isLoading = false;
        },
        () => {
          this.sharedService.showErrorFlashMessage();
          this.isLoading = false;
        }
      )
    );
  }

  testEncryption(): void {
    this.subscriptions.push(
      this.httpService.post('networks_management/encodeRpm', { rpm: this.encryptionTestSource }).subscribe(
        (response: any) => {
          this.encryptionTestTarget = response.data.encryptedValue;
        },
        () => {
          this.sharedService.showErrorFlashMessage();
          this.isLoading = false;
        }
      )
    );
  }

  validateCallbacks(): boolean {
    let valid = true;
    this.networkSettings.callbacks.forEach((callback: any) => {
      callback.url = callback.url.replace(' ', '%20');
    });
    if (!this.callbacksService.validateNoEmptyCallbacks(this.networkSettings.callbacks)) {
      valid = false;
      this.errorInputs.push('callbacksEmpty');
    } else if (!this.callbacksService.validatCallbacksURLs(this.networkSettings.callbacks)) {
      valid = false;
      this.errorInputs.push('callbacksURL');
    }
    if (!this.callbacksService.validateIVTCallbacks(this.networkSettings.callbacks)) {
      valid = false;
      this.errorInputs.push('callbacksIVT');
    }
    if (!this.callbacksService.validateUniqueCallbacks(this.networkSettings.callbacks)) {
      valid = false;
      this.errorInputs.push('callbacksDoubled');
    }
    if (!this.callbacksService.validateCallbacksRates(this.networkSettings.callbacks)) {
      valid = false;
      this.errorInputs.push('callbacksRate');
    }
    return valid;
  }

  save(): void {
    const subscriptions: Array<any> = [];
    if (!_.isEqual(this.networkSettings.prebid, this.originalData.prebid)) {
      subscriptions.push(this.savePreBid());
    }
    if (
      !_.isEqual(this.networkSettings.postbid, this.originalData.postbid) ||
      !_.isEqual(this.networkSettings.ivt, this.originalData.ivt)
    ) {
      subscriptions.push(this.savePostBid());
    }
    if (!_.isEqual(this.networkSettings.callbacks, this.originalData.callbacks)) {
      this.errorInputs = [];
      subscriptions.push(this.saveCallbacks());
    }
    if (
      this.isUserAdmin &&
      this.userService.getFeatureState('network_prebid_json') &&
      !_.isEqual(this.networkSettings.prebidBundle, this.originalData.prebidBundle)
    ) {
      subscriptions.push(this.savePreBidBundle());
    }
    if (subscriptions.length > 0) {
      this.isLoading = true;
      forkJoin(subscriptions).subscribe((success: Array<boolean>) => {
        this.isLoading = false;
        if (!success.includes(false)) {
          this.originalData = _.cloneDeep(this.networkSettings);
        }
      });
    }
  }

  savePreBid(): Observable<any> {
    const params: any = {
      anonymous: this.networkSettings.prebid.anonymous,
      fraud: this.networkSettings.prebid.fraud,
    };
    return Observable.create((observer: Observer<any>) => {
      this.subscriptions.push(
        this.httpService.post('vdsp_ws/setNetworkPreBidSettings', params).subscribe(
          (response: any) => {
            if (response.status !== 'ERROR') {
              this.sharedService.showFlashMessage(
                this.dictionaryService.get('PREBID_SETTINGS_SAVED_SUCCESSFULLY'),
                'success'
              );
              observer.next(true);
            } else {
              observer.next(false);
            }
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          }
        )
      );
    });
  }

  savePostBid(): Observable<any> {
    let value: any =
      this.networkSettings.postbid && this.postbidValues[this.networkSettings.ivt[0].rate]
        ? this.postbidValues[this.networkSettings.ivt[0].rate]
        : 'Disabled';

    if (_.isObject(value)) { value = value.value; }

    const params: any = {
      Moat: {
        rate: value,
      },
    };
    if (this.approved) {
      params.approved = true;
    }
    return Observable.create((observer: Observer<any>) => {
      this.subscriptions.push(
        this.httpService.post('vdsp_ws/setNetworkIvt', params).subscribe(
          (response: any) => {
            if (response.status === 'ERROR') {
              if (response.message === 'need approve') {
                this.modalsService.showModal({
                  component: 'postbid-opt-out',
                  title: this.dictionaryService.get('POSTBID_OPT_OUT_TITLE'),
                });
              }
              observer.next(false);
            } else if (response.status == 'OK') {
              this.sharedService.showFlashMessage(
                this.dictionaryService.get('POSTBID_SETTINGS_SAVED_SUCCESSFULLY'),
                'success'
              );
              observer.next(true);
            }
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          }
        )
      );
    });
  }

  saveCallbacks(): void {
    const isValid: boolean = this.validateCallbacks();
    let params: any = {};
    const callbacks: Array<any> = [];
    if (isValid) {
      for (let i = 0; i < this.networkSettings.callbacks.length; i++) {
        if (
          this.networkSettings.callbacks[i].url.indexOf('http://') !== 0 &&
          this.networkSettings.callbacks[i].url.indexOf('https://') !== 0
        ) {
          this.networkSettings.callbacks[i].url = 'http://' + this.networkSettings.callbacks[i].url;
        }
        callbacks.push({
          url: this.networkSettings.callbacks[i].url,
          eventId: this.networkSettings.callbacks[i].event.eventId,
          typeId: this.networkSettings.callbacks[i].event.typeId,
          rate: this.networkSettings.callbacks[i].rate,
        });
        if (this.networkSettings.callbacks[i].callbackId) {
          callbacks[i].callbackId = this.networkSettings.callbacks[i].callbackId;
        }
      }
      params = { callbacks: callbacks };
      return Observable.create((observer: Observer<any>) => {
        this.subscriptions.push(
          this.httpService.post('networks_management/saveCallbacks', params).subscribe(
            (response: any) => {
              if (response.status === 'ERROR') {
                this.sharedService.showErrorFlashMessage(response.errors);
              } else {
                this.sharedService.showFlashMessage(
                  this.dictionaryService.get('NETWORK_CALLBACKS_SAVED_SUCCESSFULLY'),
                  'success'
                );
                this.getNetworkCallbacks();
              }
              observer.next(true);
              observer.complete();
            },
            () => {
              observer.next(false);
              observer.complete();
            }
          )
        );
      });
    } else {
      return Observable.create((observer: Observer<any>) => {
        this.sharedService.onEntitySaveFailure();
        observer.next(false);
        observer.complete();
      });
    }
  }

  savePreBidBundle(): void {
    const params: any = {
      url: encodeURI(this.networkSettings.prebidBundle.url),
      json: this.networkSettings.prebidBundle.json ? stringify(JSON.parse(this.networkSettings.prebidBundle.json)) : '',
    };
    return Observable.create((observer: Observer<any>) => {
      this.subscriptions.push(
        this.httpService.post('vdsp_ws/setNetworkPreBidBundle', params).subscribe(
          (response: any) => {
            if (response.status !== 'ERROR') {
              this.sharedService.showFlashMessage(
                this.dictionaryService.get('PREBID_BUNDLE_SAVED_SUCCESSFULLY'),
                'success'
              );
              observer.next(true);
            } else {
              this.onSaveError(response.message);
              observer.next(false);
            }
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          }
        )
      );
    });
  }

  onSaveError(errors: any = {}) {
    this.sharedService.flashMessages = [];
    this.sharedService.flashMessagesIndex = 0;
    this.sharedService.showErrorFlashMessage(errors);
  }

  validatePrebidBundle(): boolean {
    let valid = true;
    this.errorInputs = _.without(this.errorInputs, 'prebid_json', 'prebid_json_too_long');

    if (!this.sharedService.isJSON(this.networkSettings.prebidBundle.json)) {
      valid = false;
      this.errorInputs.push('prebid_json');
    }
    this.errorInputs = _.uniq(this.errorInputs);
    return valid;
  }

  formatPrebidBundle($event: any): void {
    if ($event && $event.target && $event.target.value && this.validatePrebidBundle()) {
      setTimeout(() => {
        this.networkSettings.prebidBundle.json = stringify(JSON.parse($event.srcElement.value), {
          space: this.sharedService.formatJSONstring,
        });
      }, 150);
    }
  }

  switchFraudSettings(): void {
    let msg: string;
    this.networkSettings.prebid.fraud = !this.networkSettings.prebid.fraud;
    msg = this.networkSettings.prebid.fraud ? 'ALL_DEMANDS_WILL_FILTER' : 'ALL_DEMANDS_WILL_NOT_FILTER';
    this.sharedService.showFlashMessage(this.dictionaryService.get(msg), 'success');
  }

  isShouldShowPostbid(): boolean {
    return !this.userService.getFeatureState('post_bid')
      ? false
      : !(this.userService.isPublisher() || this.userService.isPublisherNetwork())
      ? true
      : this.userService.getFeatureState('post_bid_for_publishers');
  }

  close(): void {
    this.location.back();
  }

  hasChanges(): boolean {
    return !_.isEqual(this.networkSettings, this.originalData);
  }

  @HostListener('document:wheel', ['$event'])
  onScroll(): void {
    let found = false;
    let index = 0;
    const scrollY: number = window.scrollY;
    const sections: HTMLElement = document.getElementById('sections');
    let currSection: HTMLElement;
    if (sections) {
      while (!found && index < this.allSections.length - 1) {
        currSection = document.getElementById(this.allSections[index].value);
        if (currSection) {
          found = currSection.offsetTop >= scrollY;
        }
        if (!found) {
          index++;
        }
      }
    }
    this.activeSection = index;
  }
}
